import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import DomainSidebar from './DomainSidebar';
import PracticeSection from './PracticeSection';
import SubmitFooter from './SubmitFooter';
import ConfirmationModal from './ConfirmationModal';
import { useCobitAssessment } from '../../hooks/useCobitAssessment';

const PREVIEW_INCOMPLETE_COUNT = 6;
const DARK_MODE_KEY = 'cobit_dark_mode';

const COBITAssessmentPage = () => {
  const navigate = useNavigate();

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem(DARK_MODE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(DARK_MODE_KEY, JSON.stringify(darkMode));
  }, [darkMode]);

  const {
    domains,
    responses,
    selectedDomain,
    selectedDomainId,
    setSelectedDomainId,
    submissionStatus,
    loading,
    error,
    submitting,
    lastSaved,
    progress,
    incompleteQuestions,
    submissionState,
    saveState,
    isBlockedByAuth,
    isOffline,
    handleResponse,
    handleEvidence,
    handleFilesSelected,
    retryPendingFile,
    removePendingFile,
    handleRemoveBackendFile,
    submitCurrentDomain,
    getDomainProgress,
  } = useCobitAssessment(navigate);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState(null);
  const [showAllIncompleteQuestions, setShowAllIncompleteQuestions] = useState(false);
  const [isDomainPanelCollapsed, setIsDomainPanelCollapsed] = useState(false);

  const isLocked = selectedDomain && submissionStatus[selectedDomain.id] === 'submitted';

  const visibleIncompleteQuestions = useMemo(
    () =>
      showAllIncompleteQuestions
        ? incompleteQuestions
        : incompleteQuestions.slice(0, PREVIEW_INCOMPLETE_COUNT),
    [showAllIncompleteQuestions, incompleteQuestions]
  );

  const scrollToQuestion = useCallback((questionId) => {
    const el = document.getElementById(`question-${questionId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedQuestionId(questionId);
    setTimeout(() => {
      setHighlightedQuestionId((prev) => (prev === questionId ? null : prev));
    }, 2000);
  }, []);

  const theme = {
    page: darkMode ? 'bg-gray-950 text-gray-100' : 'bg-white text-gray-900',
    panel: darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white',
    panelMuted: darkMode ? 'bg-gray-800 border-gray-700' : 'bg-orange-50',
    heading: darkMode ? 'text-orange-300' : 'text-orange-800',
    title: darkMode ? 'text-orange-400' : 'text-orange-700',
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    mutedText: darkMode ? 'text-gray-300' : 'text-gray-700',
    questionCard: darkMode ? 'bg-gray-900 border border-gray-800 hover:shadow-lg' : 'bg-white hover:shadow-lg',
    lockedCard: darkMode ? 'bg-gray-900 border border-gray-800 opacity-75' : 'bg-gray-50 opacity-75',
    input:
      darkMode
        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
    disabledInput: darkMode
      ? 'bg-gray-800 cursor-not-allowed border-gray-700 text-gray-400'
      : 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-500',
    progressTrack: darkMode ? 'bg-gray-700' : 'bg-gray-300',
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${
          darkMode
            ? 'bg-gradient-to-br from-gray-950 to-gray-900'
            : 'bg-gradient-to-br from-amber-50 to-orange-50'
        }`}
      >
        <div
          className={`rounded-2xl shadow-xl p-8 text-center ${
            darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-orange-100'
          }`}
        >
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
            Loading COBIT Assessment
          </h2>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Please wait while we prepare your assigned processes...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme.page}`}>
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme.page}`}>
      <div className="max-w-5xl mx-auto px-4 py-8 mb-16">
        <div className="flex justify-between items-center mb-6 gap-3">
          <div className="w-24" />
          <h1 className={`text-3xl font-bold text-center ${theme.title}`}>COBIT Assessment</h1>

          <div className="w-24 flex justify-end">
            <button
              type="button"
              onClick={() => setDarkMode((prev) => !prev)}
              className={`relative inline-flex h-11 w-24 items-center rounded-full transition-all duration-300 border shadow-sm ${
                darkMode ? 'bg-gray-800 border-gray-600' : 'bg-orange-100 border-orange-300'
              }`}
              aria-label="Toggle dark mode"
            >
              <span
                className={`absolute left-1 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md transition-transform duration-300 ${
                  darkMode ? 'translate-x-[52px]' : 'translate-x-0'
                }`}
              >
                {darkMode ? '🌙' : '☀️'}
              </span>
              <span
                className={`w-full text-xs font-bold ${
                  darkMode ? 'text-gray-200 pl-3 text-left' : 'text-orange-800 pr-3 text-right'
                }`}
              >
                {darkMode ? 'DARK' : 'LIGHT'}
              </span>
            </button>
          </div>
        </div>

        {(isBlockedByAuth || isOffline || saveState?.hasUnsyncedChanges) && (
          <div
            className={`mb-4 rounded-lg border px-4 py-3 ${
              isBlockedByAuth
                ? darkMode
                  ? 'bg-red-900/30 border-red-700 text-red-100'
                  : 'bg-red-50 border-red-200 text-red-800'
                : isOffline
                ? darkMode
                  ? 'bg-yellow-900/30 border-yellow-700 text-yellow-100'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                : darkMode
                ? 'bg-blue-900/25 border-blue-700 text-blue-100'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <p className="font-semibold text-sm">
              {isBlockedByAuth
                ? '⚠️ Session expired. Your latest changes are not being saved. Please sign in again.'
                : isOffline
                ? '⚠️ You are offline. Changes may not sync until connection is restored.'
                : '⚠️ You have unsynced changes.'}
            </p>
            {saveState?.lastError && <p className="text-xs mt-1 opacity-90">{saveState.lastError}</p>}
          </div>
        )}

        {selectedDomain && (
          <DomainSidebar
            domains={domains}
            selectedDomainId={selectedDomainId}
            onSelectDomain={setSelectedDomainId}
            getDomainProgress={getDomainProgress}
            submissionStatus={submissionStatus}
            isCollapsed={isDomainPanelCollapsed}
            setIsCollapsed={setIsDomainPanelCollapsed}
            selectedProgress={progress}
            isLocked={isLocked}
            darkMode={darkMode}
            theme={theme}
          />
        )}

        {selectedDomain && (
          <>
            {(selectedDomain.practices || []).map((practice) => (
              <PracticeSection
                key={practice.id}
                practice={practice}
                responses={responses}
                isLocked={isLocked}
                highlightedQuestionId={highlightedQuestionId}
                onAnswerChange={handleResponse}
                onEvidenceChange={handleEvidence}
                onFilesSelected={handleFilesSelected}
                onRemoveBackendFile={handleRemoveBackendFile}
                onRetryPendingFile={retryPendingFile}
                onRemovePendingFile={removePendingFile}
                darkMode={darkMode}
                theme={theme}
              />
            ))}

            {!isLocked && incompleteQuestions.length > 0 && (
              <div
                className={`mb-5 rounded-lg border p-4 ${
                  darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className={`font-semibold ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                    ⚠️ {incompleteQuestions.length} question{incompleteQuestions.length !== 1 ? 's' : ''} still incomplete
                  </p>
                  {incompleteQuestions.length > PREVIEW_INCOMPLETE_COUNT && (
                    <button
                      type="button"
                      onClick={() => setShowAllIncompleteQuestions((prev) => !prev)}
                      className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:opacity-90"
                    >
                      {showAllIncompleteQuestions ? 'Show less' : `Show all (${incompleteQuestions.length})`}
                    </button>
                  )}
                </div>

                <div className="mt-3 space-y-2 max-h-60 overflow-auto">
                  {visibleIncompleteQuestions.map((item) => (
                    <button
                      key={item.questionId}
                      type="button"
                      onClick={() => scrollToQuestion(item.questionId)}
                      className={`w-full text-left p-2 rounded border text-sm transition ${
                        darkMode
                          ? 'border-gray-700 bg-gray-900 hover:bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-800'
                      }`}
                    >
                      <div>
                        <span className="font-semibold">Q{item.order}</span> — {item.text}
                      </div>
                      <div className="text-xs mt-1 text-red-400">{item.reason}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <SubmitFooter
              isLocked={isLocked}
              submitting={submitting}
              canSubmit={submissionState.canSubmit}
              submitReason={submissionState.reason}
              pendingUploads={submissionState.pendingUploads}
              onOpenConfirm={() => setShowConfirmModal(true)}
              onGoDashboard={() => navigate('/cobit/dashboard')}
              lastSaved={lastSaved}
              theme={theme}
            />
          </>
        )}

        <ConfirmationModal
          open={showConfirmModal}
          domainTitle={selectedDomain?.title}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={async () => {
            if (!submissionState.canSubmit) return;
            setShowConfirmModal(false);
            await submitCurrentDomain();
          }}
          darkMode={darkMode}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default COBITAssessmentPage;